"use client";

import { useEffect } from "react";

const DESKTOP_QUERY = "(min-width: 768px)";

function visibleRects(nav: HTMLElement): DOMRect[] {
    return Array.from(nav.children)
        .map((child) => child.getBoundingClientRect())
        .filter((rect) => rect.width > 0 || rect.height > 0);
}

/**
 * Height of the navigation stack (brand row + primary header + optional secondary header).
 * Measured from its visible children because on mobile the sticky variants render the wrapper
 * as `display: contents`, which has no box of its own, and the brand row is `display: none`.
 */
export function measureNavigationHeight(nav: HTMLElement): number {
    if (nav.children.length === 0) return nav.offsetHeight;
    const rects = visibleRects(nav);
    if (rects.length === 0) return 0;
    return rects[rects.length - 1].bottom - rects[0].top;
}

/**
 * Height pinned to the top of the viewport while scrolling: the whole stack on desktop,
 * only the `<header>` on mobile, nothing for non-sticky variants.
 */
export function measureStickyNavOffset(nav: HTMLElement, sticky: boolean, isDesktop: boolean): number {
    if (!sticky) return 0;
    if (isDesktop) return measureNavigationHeight(nav);
    const header = Array.from(nav.children).find((child) => child.tagName === "HEADER");
    if (!header) return 0;
    const rect = header.getBoundingClientRect();
    return rect.bottom - rect.top;
}

/**
 * Exposes the navigation's size on <html>:
 * - --nav-total-height: the whole stack, so fixed elements like wallpaper ads sit below it
 * - --sticky-nav-offset: the pinned part, so in-page sticky elements (tab bars, sidebars) stop below it
 */
export function NavHeightTracker({ sticky }: { sticky: boolean }) {
    useEffect(() => {
        const nav = document.getElementById("site-navigation");
        if (!nav) return;
        const desktop = window.matchMedia(DESKTOP_QUERY);
        const root = document.documentElement.style;

        const update = () => {
            root.setProperty("--nav-total-height", `${measureNavigationHeight(nav)}px`);
            root.setProperty("--sticky-nav-offset", `${measureStickyNavOffset(nav, sticky, desktop.matches)}px`);
        };

        // Observe the children too: a display: contents wrapper never reports a resize itself.
        const ro = new ResizeObserver(update);
        ro.observe(nav);
        for (const child of Array.from(nav.children)) ro.observe(child);
        desktop.addEventListener("change", update);
        update();

        return () => {
            ro.disconnect();
            desktop.removeEventListener("change", update);
        };
    }, [sticky]);

    return null;
}
